// Global utils
import AuthUtils from 'utils/auth';
import {normalizeOrderMerchant} from 'utils/order';

import {store} from 'store';

// Request utils
import request, {REQUEST_ENV} from 'request';
import {setTurnOffReasons} from 'store/actions';
import {isWebClient} from 'utils';

export const getTurnOffReasonsRequest = async setState => {
	try {
		const locationId = store.getState().reducerUser?.selectedLocation?.id;
		const {data} = await request(
			{
				method: 'get',
				url: `${REQUEST_ENV.ORDERHUB_API_HOST}/menu-availability/reason`,
				headers: {
					Authorization: `Bearer ${AuthUtils.sessionToken}`,
				},
				params: isWebClient ? {} : {locationId},
			},
			setState,
		);
		store.dispatch(setTurnOffReasons(data));
	} catch {}
};

export const getScheduledOrderList = async () => {
	const response = await request(
		{
			method: 'get',
			url: `${REQUEST_ENV.POS_API_HOST}/order/scheduled/list`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
		},
	);
	const wrapped = wrapOrders(response);
	if (!Array.isArray(wrapped?.data?.orders)) return wrapped;
	return {...wrapped, data: {...wrapped.data, orders: wrapped.data.orders.map(normalizeOrderMerchant)}};
};

export const getOrderListRequest = async (payload, setState) => {
	// payload: {orderDate, locationId, status, limit, offset, sortBy, sortOrder, sessionId?}
	const response = await request(
		{
			method: 'get',
			url: `${REQUEST_ENV.POS_API_HOST}/order/list`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			params: isWebClient
				? {orderStatus: payload?.orderStatus === 'processed' ? 'processing' : payload?.orderStatus}
				: {...payload},
		},
		setState,
	);
	const wrapped = wrapOrders(response);
	if (!Array.isArray(wrapped?.data?.orders)) return wrapped;
	return {...wrapped, data: {...wrapped.data, orders: wrapped.data.orders.map(normalizeOrderMerchant)}};
};

// The online API has returned the recon rows both as a bare array and as {data: [...]};
// callers destructure `data`, so accept either rather than crash on an undefined list.
const unwrapRows = response => (Array.isArray(response) ? {success: true, data: response} : response);

// The online API returns order lists as a bare {orders: [...]}; callers read `response.data.orders`.
const wrapOrders = response => (response && !Array.isArray(response) && !('data' in response) && 'orders' in response
	? {success: true, data: response}
	: response);

export const getReconSummaryRequest = async (payload, setState) => {
	const locationId = store.getState().reducerUser?.selectedLocation?.id;
	// payload: {searchString, startTime, endTime}
	const response = await request(
		{
			method: 'get',
			url: `${REQUEST_ENV.ORDERHUB_API_HOST}/order/summary`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			params: isWebClient ? {...payload} : {locationId, ...payload},
			timeout: 60000,
		},
		setState,
	);
	return unwrapRows(response);
};
export const getReconDetailsRequest = async (payload, setState) => {
	const locationId = store.getState().reducerUser?.selectedLocation?.id;
	// payload: {locationId, startTime, endTime, brandId, platformId}
	const response = await request(
		{
			method: 'get',
			url: `${REQUEST_ENV.ORDERHUB_API_HOST}/order/summary/list`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			params: isWebClient ? {...payload} : {locationId, limit: 999999, ...payload},
			timeout: 60000,
		},
		setState,
	);
	const rows = unwrapRows(response);
	if (!Array.isArray(rows?.data)) return rows;
	return {...rows, data: rows.data.map(normalizeOrderMerchant)};
};

// export const processOrderRequest = async (payload, setState) => {
// 	// payload: {orderId, processedBy}
// 	return request(
// 		{
// 			method: 'put',
// 			url: `${REQUEST_ENV.ORDERHUB_API_HOST}/resto/order/process`,
// 			headers: {
// 				Authorization: `Bearer ${AuthUtils.sessionToken}`,
// 			},
// 			data: payload,
// 		},
// 		setState,
// 	);
// };

export const processOrderRequest = async (payload, setState) => {
	// payload: {orderId, auto}
	/**
	 * auto: true -> auto process
	 * auto: false -> manual process, pair later
	 */
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.POS_API_HOST}/order/process`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: payload,
		},
		setState,
	);
};

export const processScheduleOrder = async (payload, setState) => {
	// payload: {orderId}
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.POS_API_HOST}/order/scheduled/process`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: payload,
		},
		setState,
	);
};
export const markDone = async (payload, setState) => {
	// payload: {orderId}
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.POS_API_HOST}/order/done`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: payload,
		},
		setState,
	);
};

export const manualPairOrder = async (payload, setState) => {
	// payload: {orderId, pairOrderId, pairReason}
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.POS_API_HOST}/order/pair/manual`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: payload,
		},
		setState,
	);
};

export const pseudoPairOrder = async (payload, setState) => {
	// payload: {orderId, merchantRefId}
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.POS_API_HOST}/order/pair/force`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: payload,
		},
		setState,
	);
};

export const editOrderBookingId = async (payload, setState) => {
	// payload: {orderId, merchantRefId}
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.POS_API_HOST}/order/booking`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: payload,
		},
		setState,
	);
};

export const voidOrder = async (payload, setState) => {
	// payload: {orderId, pinNumber, voidReason}
	const locationId = store.getState().reducerUser?.selectedLocation?.id;
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.POS_API_HOST}/order/void`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: {...payload, locationId},
		},
		setState,
	);
};

export const flagCancelOrderRequest = async (payload, setState) => {
	// payload: {orderId, reason}
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.ORDERHUB_API_HOST}/resto/mark/cancel`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: payload,
		},
		setState,
	);
};

export const flagUpdateOrderRequest = async (payload, setState) => {
	// payload: {orderId}
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.ORDERHUB_API_HOST}/resto/order/update`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: payload,
		},
		setState,
	);
};

export const markFraud = async (payload, setState) => {
	const {externalId} = payload;
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.ORDERHUB_API_HOST}/resto/order/mark/fraudulent/${externalId}`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
		},
		setState,
	);
};

export const resyncOrder = async payload => {
	return request(
		{
			method: 'post',
			url: `${REQUEST_ENV.ORDERHUB_API_HOST}/order/sync`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: payload,
		},
	);
};

// Hangry App APIs
export const cancelHangryAppOrderRequest = async (payload, setState) => {
	// payload: {orderId, pinNumber, voidReason}
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.POS_API_HOST}/order/hangry/cancel`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: payload,
		},
		setState,
	);
};

// export const cancelHangryAppOrderRequest = async (payload, setState) => {
// 	// payload: {orderId, reason}
// 	return request(
// 		{
// 			method: 'put',
// 			url: `${REQUEST_ENV.ORDERHUB_API_HOST}/order/hangry/cancel`,
// 			headers: {
// 				Authorization: `Bearer ${AuthUtils.sessionToken}`,
// 			},
// 			data: payload,
// 		},
// 		setState,
// 	);
// };

export const findDriverHangryAppOrderRequest = async (payload, setState) => {
	// payload: {orderId}
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.ORDERHUB_API_HOST}/order/hangry/find-driver`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: payload,
		},
		setState,
	);
};

export const changeDriverHangryAppOrderRequest = async (payload, setState) => {
	// payload: {orderId}
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.ORDERHUB_API_HOST}/order/hangry/change-driver`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: payload,
		},
		setState,
	);
};

export const findDriverInternalOrderRequest = async (payload, setState) => {
	// payload: {orderId}
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.ORDERHUB_API_HOST}/order/find-driver`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: payload,
		},
		setState,
	);
};

export const changeDriverInternalOrderRequest = async (payload, setState) => {
	// payload: {orderId}
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.ORDERHUB_API_HOST}/order/change-driver`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: payload,
		},
		setState,
	);
};

export const markPickupReadyHangryAppOrderRequest = async (payload, setState) => {
	// payload: {orderId}
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.ORDERHUB_API_HOST}/order/hangry/process/ready`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: payload,
		},
		setState,
	);
};

export const updateOrder = async (payload, setState) => {
	// payload: {orderId}
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.ORDERHUB_API_HOST}/kds/orders`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: {...payload, station: 'checker', odsDoneFrom: 'pos'},
		},
		setState,
	);
};

export const autoInjectOrder = async ({orderId}, setState) => {
	// payload: {orderId}
	return request(
		{
			method: 'put',
			// url: `${REQUEST_ENV.ORDERHUB_API_HOST}/resto/order/inject`,
			url: `${REQUEST_ENV.POS_API_HOST}/order/process`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: {orderId, auto: true},
		},
		setState,
	);
};

export const markRTPU = async ({orderId}, setState) => {
	// payload: {orderId}
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.POS_API_HOST}/order/ready`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: {orderId},
		},
		setState,
	);
};

// export const triggerRecall = async ({orderId}) => {
// 	// payload: {orderId}
// 	return request(
// 		{
// 			method: 'put',
// 			url: `${REQUEST_ENV.POS_API_HOST}/order/announce`,
// 			headers: {
// 				Authorization: `Bearer ${AuthUtils.sessionToken}`,
// 			},
// 			data: {orderId},
// 		},
// 	);
// };

export const triggerRecall = async ({id}) => {
	// payload: {id}
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.POS_API_HOST}/order/announce`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: {id},
		},
	);
};

export const toggleRatingBoost = async payload => {
	// payload: {orderId, isRatingBoost}
	return request(
		{
			method: 'put',
			url: `${REQUEST_ENV.POS_API_HOST}/order/rating-boost`,
			headers: {
				Authorization: `Bearer ${AuthUtils.sessionToken}`,
			},
			data: payload,
		},
	);
};
